import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, X } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import PageMeta from "@/components/PageMeta";
import { api } from "@/lib/api";

export default function People() {
  const [lawyers, setLawyers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/lawyers").then((r) => setLawyers(r.data || [])).catch(() => setLawyers([]));
    api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    return lawyers.filter((l) => {
      if (filter !== "all" && l.department?.slug !== filter) return false;
      if (!q) return true;
      const hay = [
        l.name,
        l.role,
        l.title,
        l.department?.title,
        ...(l.tag_services || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [lawyers, filter, q]);

  const initials = (name = "") => name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("");

  return (
    <SiteLayout>
      <PageMeta
        title="People — Carlton Elleker Chase"
        description="Meet the lawyers of Carlton Elleker Chase. Filter by department or search by name and area of expertise."
        path="/people"
      />

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-12 md:pt-24" data-testid="people-page">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <h1 className="h-display text-[clamp(2.4rem,6vw,5rem)] max-w-[18ch]">
              The lawyers of<br />Carlton Elleker Chase.
            </h1>
          </div>

          {/* Slick search */}
          <div className="md:col-span-5">
            <label className="block">
              <span className="overline text-graphite/55">Find someone</span>
              <div className={`mt-3 flex items-center gap-3 px-5 py-3.5 bg-warmwhite border transition-all ${q ? "border-[#021D6F]" : "border-graphite/15 hover:border-graphite/35"}`}>
                <Search size={18} className="text-graphite/55" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or expertise…"
                  className="flex-1 bg-transparent outline-none text-base text-graphite placeholder:text-graphite/45"
                  data-testid="people-search"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="text-graphite/55 hover:text-graphite"
                    aria-label="Clear search"
                    data-testid="people-search-clear"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Department filter chips */}
        <div className="mt-12 hairline-top hairline-bottom py-5 flex flex-wrap items-center gap-2" data-testid="people-filters">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            All <span className="ml-1.5 text-[10px] text-graphite/45 align-top">{lawyers.length}</span>
          </FilterChip>
          {departments.map((d) => {
            const count = lawyers.filter((l) => l.department?.slug === d.slug).length;
            return (
              <FilterChip key={d.slug} active={filter === d.slug} onClick={() => setFilter(d.slug)} testid={`filter-${d.slug}`}>
                {d.title} <span className="ml-1.5 text-[10px] text-graphite/45 align-top">{count}</span>
              </FilterChip>
            );
          })}
        </div>

        {/* Results meta */}
        <p className="mt-6 text-xs uppercase tracking-[0.16em] text-graphite/55" data-testid="people-result-meta">
          Showing {filtered.length} of {lawyers.length}
          {filter !== "all" && (
            <> · <span className="text-[#1E3668]">{departments.find((d) => d.slug === filter)?.title}</span></>
          )}
          {q && <> · matching <span className="text-[#1E3668]">"{q}"</span></>}
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-20">
        <div
          className="grid gap-x-5 gap-y-10 md:gap-x-6 md:gap-y-14 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
          data-testid="people-grid"
        >
          {filtered.map((l) => (
            <Link
              to={`/people/${l.slug}`}
              key={l.slug}
              className="group block"
              data-testid={`person-${l.slug}`}
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#EEEAE3]">
                {l.photo_url ? (
                  <img
                    src={l.photo_url}
                    alt={l.name}
                    loading="lazy"
                    className="w-full h-full object-cover bw-portrait"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-graphite/40 text-3xl font-light">
                    {initials(l.name)}
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[0.95rem] font-semibold tracking-tight leading-tight truncate">{l.name}</p>
                  <p className="text-xs text-graphite/60 mt-0.5 truncate">
                    {l.department?.title || l.title || l.role}
                  </p>
                </div>
                <ArrowUpRight size={14} className="mt-1 text-graphite/40 group-hover:text-[#1E3668] transition-colors flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-24" data-testid="people-empty">
            <p className="text-graphite/65">No lawyers match this search.</p>
            <button
              onClick={() => { setQuery(""); setFilter("all"); }}
              className="mt-4 link-grow text-sm font-medium tracking-[0.16em] uppercase"
            >
              Reset filters
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}

function FilterChip({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[0.78rem] tracking-[0.12em] uppercase px-3.5 py-1.5 transition-all border ${
        active
          ? "bg-[#021D6F] text-warmwhite border-[#021D6F]"
          : "bg-transparent text-graphite/70 border-graphite/15 hover:border-graphite/40 hover:text-graphite"
      }`}
    >
      {children}
    </button>
  );
}
